import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormHelperText,
  Grid,
  IconButton,
  Paper,
  Stack,
  Typography
} from '@mui/material';
import NepaliDatePicker from 'components/ui/Datepicker/Datepicker';
import { useFormik } from 'formik';
import { BiCamera } from 'react-icons/bi';
import { toast } from 'react-toastify';
import {
  useCurrentUserDetailsQuery,
  usePersonalDetailQuery,
  useUpdatePersonalDetailMutation
} from 'redux/reducers/user/dashboard/tms-user';
import {
  useDistrictsQuery,
  useEthnicityQuery
} from 'redux/reducers/user/dashboard/tms-user-location';
import { convertToNepali } from 'utils/nepali-convert';
import SelectInput from '../components/Select';
import { FIELD_SIZE, Section, StickyActions } from '../components/Section';
import TextInput from '../components/TextInput';
import {
  CastOptions,
  genderOptions,
  IPersonalDetails,
  isBankDetailRequired,
  MaritalStatusOptions,
  PersonalDetailsInitialValues,
  PersonalDetailsValidationSchema,
  ReligionOptions
} from './Schema';
import './style.css';

import { BSToAD } from 'components/ui/Datepicker/Datepickerutils';
import getDoaminName from 'lib/getDomainName';
import moment from 'moment';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { BsFileEarmarkText, BsUpload } from 'react-icons/bs';
import { backendURL, nascsubdomain, trainingsubdomain } from 'redux/reducers/settings';

const SUPPORTED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/jpg'];

/** BS date string -> its AD equivalent, or '' when the BS date is empty or unparseable. */
function toAdDate(bsDate?: string) {
  if (!bsDate) return '';
  try {
    const adDate = moment(BSToAD(bsDate));
    return adDate.isValid() ? adDate.format('YYYY-MM-DD') : '';
  } catch {
    return '';
  }
}

/** AD readout shown inside a BS date input, right of the typed value. */
function AdDateSuffix({ date }: { date: string }) {
  return <span className="ad-date-suffix">AD {date}</span>;
}

function PersonalInfo({
  activeStep,
  setActiveStep
}: {
  activeStep: number;
  setActiveStep: (step: number) => void;
}) {
  const { data: ethnicityList } = useEthnicityQuery();

  const Ethnicities =
    ethnicityList?.map((e) => ({
      label: e.name,
      value: e.id.toString()
    })) ?? [];

  const { data: CurrentUser, isLoading: isCurrentUserLoading } = useCurrentUserDetailsQuery();
  const TraineeId = CurrentUser?.id;
  const formik = useFormik({
    enableReinitialize: true,
    validationSchema: PersonalDetailsValidationSchema,
    initialValues: {
      ...PersonalDetailsInitialValues,
      bank_name: '',
      bank_account_holder: '',
      bank_account_number: '',
      bank_branch: '',
      pan_number: 0
    },
    onSubmit: async (values, { setSubmitting }) => {
      const bsDate = values.citizen_ship_issued_date_bs;

      const adDate = bsDate ? moment(BSToAD(bsDate)).format('YYYY-MM-DD') : '';

      const sendValue = {
        ...values,
        citizen_ship_issued_date: adDate,
        citizen_ship_issued_date_bs: bsDate,
        dob_bs: values.dob_bs ?? '',
        employee_sn: values.employee_id
      };

      try {
        await UpdatePersonalDetail(sendValue as unknown as IPersonalDetails);
        toast.success('User Updated Successfully');
        // setActiveStep(activeStep + 1);
      } catch (err: any) {
        err?.response?.data?.data?.non_field_errors?.map((error: any) => {
          toast.error(error);
        });
      } finally {
        setSubmitting(false);
      }
    }
  });
  usePersonalDetailQuery(TraineeId ?? '', (data) => {
    formik.setValues({
      ...PersonalDetailsInitialValues,
      ...data?.data,
      first_name: data?.data?.first_name ?? '',
      middle_name: data?.data?.middle_name ?? '',
      last_name: data?.data?.last_name ?? '',
      first_name_np: data?.data?.first_name_np ?? '',
      middle_name_np: data?.data?.middle_name_np ?? '',
      last_name_np: data?.data?.last_name_np ?? '',
      gender: data?.data?.gender ?? '',
      dob: data?.data?.dob ?? '',
      dob_bs: data?.data?.dob_bs ?? '',
      marital_status: data?.data?.marital_status ?? '',
      ethnicity: data?.data?.ethnicity_detail?.id ? data.data.ethnicity_detail.id.toString() : '',

      caste: data?.data?.caste ?? '',
      religion: data?.data?.religion ?? '',
      citizen_ship_no: data?.data?.citizen_ship_no ?? '',
      // citizen_ship_issued_date: data?.data?.citizen_ship_issued_date ?? '',
      province_id: null,
      district_id: null,
      employee_id: data?.data?.employee_id ?? '',
      employee_sn: data?.data?.employee_sn ?? '',
      citizen_ship_issued_date_bs: data?.data?.citizen_ship_issued_date_bs ?? '',
      citizen_ship_issued_place: data?.data?.citizen_ship_issued_place ?? '',
      bank_account_holder: data?.data?.bank_account_holder ?? '',
      bank_account_number: data?.data?.bank_account_number ?? '',
      bank_branch: data?.data?.bank_branch ?? ''
    });
  });

  const { mutateAsync: UpdatePersonalDetail, isLoading: isUpdatingPersonalDetail } =
    useUpdatePersonalDetailMutation(TraineeId ?? '');

  const { data: Districts } = useDistrictsQuery();

  const DistrictOptions =
    Districts?.map((district) => ({
      label: district.name_en,
      value: district.id.toString()
    })) ?? [];

  const image = formik.values?.image as File | string | null;
  const recommendationLetter = formik.values?.recommendation_letter as unknown as File | string;

  // A freshly picked file is previewed from memory, a saved one from the server.
  const [localImageUrl, setLocalImageUrl] = useState<string>('');
  useEffect(() => {
    if (!(image instanceof File)) {
      setLocalImageUrl('');
      return;
    }
    const objectUrl = URL.createObjectURL(image);
    setLocalImageUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [image]);

  const imagePreview = useMemo(() => {
    if (image instanceof File) return localImageUrl;
    if (typeof image === 'string' && image) {
      return `${backendURL}/${image.replace(/^\//, '')}`;
    }
    return '';
  }, [image, localImageUrl]);

  const fullName = [formik.values.first_name, formik.values.middle_name, formik.values.last_name]
    .filter(Boolean)
    .join(' ');

  const showDobError = Boolean(formik.errors.dob) && (formik.touched.dob || formik.submitCount > 0);
  const citizenshipIssuedDateAd = toAdDate(formik.values.citizen_ship_issued_date_bs);
  const isSaving = isUpdatingPersonalDetail || formik.isSubmitting;

  if (isCurrentUserLoading) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 320 }}>
        <CircularProgress />
      </Stack>
    );
  }
  const domain = getDoaminName()


  return (
    <form onSubmit={formik.handleSubmit}>
      {/* Profile header */}
      <Paper
        variant="outlined"
        sx={{
          p: { xs: 2, sm: 3 },
          mb: 3,
          borderRadius: 2,
          borderColor: 'divider'
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={{ xs: 2, sm: 3 }}
          alignItems="center"
          textAlign={{ xs: 'center', sm: 'left' }}
        >
          <Box sx={{ position: 'relative', flexShrink: 0 }}>
            <Avatar
              src={imagePreview || undefined}
              alt={fullName || 'profile_image'}
              sx={{
                height: { xs: 96, sm: 120 },
                width: { xs: 96, sm: 120 },
                fontSize: { xs: 32, sm: 40 },
                bgcolor: 'grey.200',
                color: 'grey.600'
              }}
            >
              {fullName ? fullName.charAt(0).toUpperCase() : null}
            </Avatar>

            <IconButton
              component="label"
              htmlFor="img-file-input"
              aria-label="Upload profile image"
              size="small"
              sx={{
                position: 'absolute',
                right: 0,
                bottom: 0,
                bgcolor: 'primary.main',
                color: 'common.white',
                border: '2px solid',
                borderColor: 'background.paper',
                '&:hover': { bgcolor: 'primary.dark' }
              }}
            >
              <BiCamera />
              <input
                hidden
                id="img-file-input"
                type="file"
                accept="image/png, image/jpeg, image/jpg"
                onChange={(e) => {
                  const file = e.target.files?.[0];

                  if (file) {
                    // Extra validation check in case user bypasses the filter
                    if (SUPPORTED_IMAGE_TYPES.includes(file.type)) {
                      formik.setFieldValue('image', file);
                    } else {
                      toast.error('Please upload a valid image (PNG, JPG, or JPEG)');
                      e.target.value = ''; // Reset the input
                    }
                  }
                }}
              />
            </IconButton>
          </Box>

          <Stack spacing={1} alignItems={{ xs: 'center', sm: 'flex-start' }} sx={{ minWidth: 0 }}>
            <Typography variant="h6" fontWeight={600} sx={{ wordBreak: 'break-word' }}>
              {fullName || 'Profile Image'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              PNG, JPG or JPEG. A square photo looks best.
            </Typography>
            {image instanceof File && (
              <Chip
                label={image.name}
                size="small"
                onDelete={() => formik.setFieldValue('image', null)}
                sx={{ maxWidth: 260 }}
              />
            )}
          </Stack>
        </Stack>
      </Paper>

      <Section
        title="Basic Information"
        description="Name and identity details as per your records."
      >
        <TextInput
          {...FIELD_SIZE}
          label="First Name"
          name="first_name"
          type="text"
          onChange={formik.handleChange}
          value={formik.values.first_name}
          onBlur={formik.handleBlur}
          formik={formik}
          required
        />
        <TextInput
          {...FIELD_SIZE}
          label="Middle Name"
          name="middle_name"
          type="text"
          onChange={formik.handleChange}
          value={formik.values.middle_name}
          onBlur={formik.handleBlur}
          formik={formik}
        />
        <TextInput
          {...FIELD_SIZE}
          label="Last Name"
          name="last_name"
          type="text"
          onChange={formik.handleChange}
          value={formik.values.last_name}
          onBlur={formik.handleBlur}
          formik={formik}
          required
        />
        <TextInput
          {...FIELD_SIZE}
          label="First Name(Nepali)"
          name="first_name_np"
          type="text"
          onChange={(e) => {
            e.target.value = convertToNepali(e.target.value);
            formik.handleChange(e);
          }}
          value={formik.values.first_name_np}
          onBlur={formik.handleBlur}
          formik={formik}
        />
        <TextInput
          {...FIELD_SIZE}
          label="Middle Name(Nepali)"
          name="middle_name_np"
          type="text"
          onChange={(e) => {
            e.target.value = convertToNepali(e.target.value);
            formik.handleChange(e);
          }}
          value={formik.values.middle_name_np}
          onBlur={formik.handleBlur}
          formik={formik}
        />
        <TextInput
          {...FIELD_SIZE}
          label="Last Name(Nepali)"
          name="last_name_np"
          type="text"
          onChange={(e) => {
            e.target.value = convertToNepali(e.target.value);
            formik.handleChange(e);
          }}
          value={formik.values.last_name_np}
          onBlur={formik.handleBlur}
          formik={formik}
        />
        <SelectInput
          {...FIELD_SIZE}
          label="Gender"
          name="gender"
          options={genderOptions}
          value={formik.values.gender}
          onChange={(e) => {
            formik.handleChange(e);
          }}
          error={
            formik.errors.gender
              ? {
                message: formik.errors.gender
              }
              : undefined
          }
          required
        />
        <Grid item {...FIELD_SIZE}>
          <Box className="nepali-date-field" sx={{ mb: 3 }}>
            <NepaliDatePicker
              containerClassName=""
              label="Date of Birth (B.S.) *"
              name="dob_bs"
              maxDateToday
              onChange={(e) => {
                formik.setFieldValue('dob_bs', e);
                formik.setFieldValue('dob', e ? moment(BSToAD(e)).format('YYYY-MM-DD') : '');
              }}
              value={formik.values.dob_bs}
            >
              {formik.values.dob ? <AdDateSuffix date={formik.values.dob} /> : null}
            </NepaliDatePicker>
            <FormHelperText error={showDobError} sx={{ mx: 1.75 }}>
              {showDobError ? formik.errors.dob : ' '}
            </FormHelperText>
          </Box>
        </Grid>

        <SelectInput
          {...FIELD_SIZE}
          label="Marital Status"
          name="marital_status"
          options={MaritalStatusOptions}
          value={formik.values.marital_status}
          onChange={(e) => {
            formik.handleChange(e);
          }}
          error={
            formik.errors.marital_status
              ? {
                message: formik.errors.marital_status
              }
              : undefined
          }
          required
        />
      </Section>

      <Section
        title="Identity & Citizenship"
        description="Demographic information and citizenship record."
      >
        <SelectInput
          {...FIELD_SIZE}
          label="Ethnicity"
          name="ethnicity"
          options={Ethnicities || []}
          value={formik.values.ethnicity}
          onChange={(e) => {
            formik.handleChange(e);
          }}
          error={
            formik.errors.ethnicity
              ? {
                message: formik.errors.ethnicity
              }
              : undefined
          }
        // required
        />

        <SelectInput
          {...FIELD_SIZE}
          label="Cast"
          name="caste"
          options={CastOptions}
          value={formik.values.caste}
          onChange={(e) => {
            formik.handleChange(e);
          }}
          error={
            formik.errors.caste
              ? {
                message: formik.errors.caste
              }
              : undefined
          }
        // required
        />
        <SelectInput
          {...FIELD_SIZE}
          label="Religion"
          name="religion"
          options={ReligionOptions}
          value={formik.values.religion}
          onChange={(e) => {
            formik.handleChange(e);
          }}
          error={
            formik.errors.religion
              ? {
                message: formik.errors.religion
              }
              : undefined
          }
        // required
        />
        <TextInput
          {...FIELD_SIZE}
          label="Citizenship No."
          name="citizen_ship_no"
          type="text"
          onChange={formik.handleChange}
          value={formik.values.citizen_ship_no}
          onBlur={formik.handleBlur}
          formik={formik}
        // required
        />
        <SelectInput
          {...FIELD_SIZE}
          label="Citizenship Issued Place"
          name="citizen_ship_issued_place"
          options={DistrictOptions}
          value={formik.values.citizen_ship_issued_place}
          onChange={(e) => {
            formik.handleChange(e);
          }}
          error={
            formik.errors.citizen_ship_issued_place
              ? {
                message: formik.errors.citizen_ship_issued_place
              }
              : undefined
          }
        />
        <Grid item {...FIELD_SIZE}>
          <Box className="nepali-date-field" sx={{ mb: 3 }}>
            <NepaliDatePicker
              containerClassName=""
              label="Citizenship Issued Date"
              name="citizen_ship_issued_date_bs"
              onChange={(e) => {
                formik.setFieldValue('citizen_ship_issued_date_bs', e);
              }}
              value={formik.values.citizen_ship_issued_date_bs}
            >
              {citizenshipIssuedDateAd ? <AdDateSuffix date={citizenshipIssuedDateAd} /> : null}
            </NepaliDatePicker>
          </Box>
        </Grid>

        <TextInput
          {...FIELD_SIZE}
          label={`Employee Id  ${getDoaminName() == nascsubdomain ? 'or PIS No.' : ''} `}
          name="employee_id"
          type="text"
          onChange={formik.handleChange}
          value={formik.values.employee_id}
          onBlur={formik.handleBlur}
          formik={formik}
        />
      </Section>

      <Section title="Bank Details" description="Used for allowance and reimbursement payments.">
        <TextInput
          {...FIELD_SIZE}
          label="Bank Name"
          name="bank_name"
          type="text"
          onChange={formik.handleChange}
          value={formik.values.bank_name}
          onBlur={formik.handleBlur}
          formik={formik}
          required={isBankDetailRequired}
        />
        <TextInput
          {...FIELD_SIZE}
          label="Account Holder Name"
          name="bank_account_holder"
          type="text"
          onChange={formik.handleChange}
          value={formik.values.bank_account_holder}
          onBlur={formik.handleBlur}
          formik={formik}
          required={isBankDetailRequired}
        />
        <TextInput
          {...FIELD_SIZE}
          label="Account Number"
          name="bank_account_number"
          type="text"
          onChange={formik.handleChange}
          value={formik.values.bank_account_number}
          onBlur={formik.handleBlur}
          formik={formik}
          required={isBankDetailRequired}
        />
        <TextInput
          {...FIELD_SIZE}
          label="Bank Branch"
          name="bank_branch"
          type="text"
          onChange={formik.handleChange}
          value={formik.values.bank_branch}
          onBlur={formik.handleBlur}
          formik={formik}
          required={isBankDetailRequired}
        />
        <TextInput
          {...FIELD_SIZE}
          label="PAN Number"
          name="pan_number"
          type="text"
          onChange={formik.handleChange}
          value={formik.values.pan_number}
          onBlur={formik.handleBlur}
          formik={formik}
          required={isBankDetailRequired}
        />
      </Section>

      <Section title="Documents" description="Upload a scanned copy of your recommendation letter.">
        <Grid item xs={12} md={8}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            sx={{
              p: 2,
              mb: 3,
              border: '1px dashed',
              borderColor: 'divider',
              borderRadius: 2
            }}
          >
            <Button
              component="label"
              htmlFor="recommendation-file-input"
              variant="outlined"
              startIcon={<BsUpload />}
              sx={{ flexShrink: 0 }}
            >
              Recommendation Letter
              <input
                hidden
                id="recommendation-file-input"
                type="file"
                onChange={(e) => {
                  formik.setFieldValue('recommendation_letter', e.target.files?.[0]);
                }}
              />
            </Button>

            <Stack spacing={0.5} sx={{ minWidth: 0 }}>
              {recommendationLetter instanceof File ? (
                <Chip
                  icon={<BsFileEarmarkText />}
                  label={recommendationLetter.name}
                  size="small"
                  onDelete={() => formik.setFieldValue('recommendation_letter', '')}
                  sx={{ maxWidth: 280 }}
                />
              ) : recommendationLetter ? (
                <Typography
                  component="a"
                  href={backendURL + recommendationLetter}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="body2"
                  sx={{ textDecoration: 'underline', cursor: 'pointer' }}
                >
                  View Recommendation Letter
                </Typography>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No file uploaded yet.
                </Typography>
              )}
            </Stack>
          </Stack>
        </Grid>
      </Section>

      <StickyActions>
        {activeStep > 1 && (
          <Button
            variant="outlined"
            color="inherit"
            onClick={() => setActiveStep(activeStep - 1)}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            Previous Step
          </Button>
        )}

        <Button
          type="submit"
          variant="contained"
          disabled={isSaving}
          startIcon={isSaving ? <CircularProgress color="inherit" size={16} /> : undefined}
          sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: 120 }}
        >
          {isSaving ? 'Saving…' : 'Save'}
        </Button>

        <Button
          variant="outlined"
          onClick={() => setActiveStep(activeStep + 1)}
          sx={{ width: { xs: '100%', sm: 'auto' } }}
        >
          Next
        </Button>
      </StickyActions>
    </form>
  );
}

export default PersonalInfo;
